import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Icon,
  VStack,
  HStack,
  Button,
  Badge,
} from '@chakra-ui/react';
import {
  FaIndustry,
  FaCogs,
  FaBoxes,
  FaArrowRight,
  FaWarehouse,
  FaTruck,
  FaClipboardCheck,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import { GiClout } from 'react-icons/gi';

import PageHero from '../Components/PageHero';
import SectionHeading from '../Components/SectionHeading';
import Reveal from '../Components/Reveal';

import industrieHeroImage from '../Components/Assets/Agrooa.webp';
import industrieImage from '../Components/Assets/indusstrie.jpeg';

function Industries() {
  const services = [
    {
      icon: GiClout,
      title: 'Production de Clous',
      description:
        'Fabrication de clous métalliques de haute qualité aux standards internationaux pour la construction et la menuiserie.',
    },
    {
      icon: FaCogs,
      title: 'Capacité de Production',
      description:
        "160 000 clous par jour, soit environ 12,5 tonnes par mois, avec un plan d'extension progressive.",
    },
    {
      icon: FaBoxes,
      title: 'Conditionnement',
      description:
        'Emballage et conditionnement professionnel pour la quincaillerie, la construction et la menuiserie.',
    },
    {
      icon: FaWarehouse,
      title: 'Stockage & Logistique',
      description:
        'Infrastructures de stockage sécurisées et solutions logistiques pour une distribution efficace.',
    },
    {
      icon: FaClipboardCheck,
      title: 'Contrôle Qualité',
      description:
        "Rigueur absolue dans le choix des matières premières et le contrôle produit.",
    },
    {
      icon: FaTruck,
      title: 'Distribution',
      description:
        'Réseau de distribution pour approvisionner les quincailleries, chantiers de construction et menuiseries.',
    },
  ];

  const stats = [
    { number: '160 000', label: 'Clous par jour', icon: GiClout },
    { number: '12,5 T', label: 'Par mois', icon: FaBoxes },
    { number: '100%', label: 'Production locale', icon: FaIndustry },
  ];

  return (
    <Box>
      {/* ═══════════════════════ HERO ═══════════════════════ */}
      <PageHero
        eyebrow="Pôle Industriel — Marché Intérieur"
        title="Des clous de qualité,"
        accent="fabriqués en RDC"
        description={
          <>
            TRIMER SARL opère une unité de production de clous métalliques destinée à
            soutenir les secteurs de la <strong>construction</strong>, de la{' '}
            <strong>menuiserie</strong> et de la <strong>quincaillerie</strong>.
          </>
        }
        image={industrieHeroImage}
        icon={GiClout}
        accentColor="navy.200"
        gradient="linear(120deg, rgba(8,28,52,0.97) 0%, rgba(15,44,77,0.92) 45%, rgba(22,60,104,0.88) 100%)"
        badges={[{ label: '160 000 clous/jour' }, { label: '12,5 tonnes/mois' }]}
        cta={{ label: 'Commander', to: '/contact', secondary: { label: 'Nos services', to: '#services' } }}
      />

      {/* ═══════════════════════ STATS ═══════════════════════ */}
      <Box bg="white" pt={{ base: 12, md: 16 }} pb={{ base: 16, md: 20 }}>
        <Container maxW="7xl">
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={{ base: 4, md: 6 }}>
            {stats.map((stat, index) => (
              <Reveal key={stat.label} delay={index * 0.08}>
                <HStack
                  spacing={{ base: 3, md: 4 }}
                  p={{ base: 5, md: 6 }}
                  bg="white"
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor="gray.100"
                  boxShadow="soft"
                  h="100%"
                  transition="all 0.35s"
                  _hover={{ transform: 'translateY(-4px)', boxShadow: 'card', borderColor: 'transparent' }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    h="52px"
                    w="52px"
                    borderRadius="xl"
                    bg="navy.50"
                    flexShrink={0}
                  >
                    <Icon as={stat.icon} boxSize={6} color="navy.600" />
                  </Box>
                  <Box minW={0}>
                    <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="800" color="gray.900" lineHeight="1.05" letterSpacing="-0.03em">
                      {stat.number}
                    </Text>
                    <Text fontSize="sm" color="gray.600" fontWeight="500">
                      {stat.label}
                    </Text>
                  </Box>
                </HStack>
              </Reveal>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ═══════════════════════ SERVICES ═══════════════════════ */}
      <Box id="services" py={{ base: 16, md: 24 }} bg="gray.50" position="relative" overflow="hidden">
        <Box position="absolute" inset={0} className="bg-grid-dark" opacity={0.5} pointerEvents="none" />

        <Container maxW="7xl" position="relative">
          <SectionHeading
            eyebrow="Notre unité de production"
            title="Ce que nous offrons"
            lead="Une unité de production moderne pour répondre aux besoins du marché national en clous métalliques de qualité."
            accent="navy.600"
          />

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
            {services.map((service, index) => (
              <Reveal key={service.title} delay={(index % 3) * 0.08}>
                <Box
                  bg="white"
                  p={8}
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor="gray.100"
                  boxShadow="soft"
                  h="100%"
                  transition="all 0.35s"
                  _hover={{ transform: 'translateY(-5px)', boxShadow: 'card', borderColor: 'transparent' }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    h="56px"
                    w="56px"
                    borderRadius="xl"
                    bg="navy.50"
                    mb={5}
                  >
                    <Icon as={service.icon} boxSize={6} color="navy.600" />
                  </Box>
                  <Heading size="md" color="gray.900" mb={3}>
                    {service.title}
                  </Heading>
                  <Text color="gray.600" fontSize="sm" lineHeight="1.8">
                    {service.description}
                  </Text>
                </Box>
              </Reveal>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ═══════════════════════ SITE ═══════════════════════ */}
      <Box py={{ base: 16, md: 24 }} bg="white">
        <Container maxW="7xl">
          <SectionHeading
            eyebrow="Notre site"
            title="Site de Production"
            accent="navy.600"
          />

          <Reveal>
            <Box
              bgGradient="linear(135deg, navy.600, navy.800)"
              p={{ base: 10, md: 14 }}
              borderRadius="2xl"
              color="white"
              textAlign="center"
              maxW="2xl"
              mx="auto"
              position="relative"
              overflow="hidden"
              boxShadow="card"
              transition="all 0.35s"
              _hover={{ transform: 'translateY(-6px)', boxShadow: 'lift' }}
            >
              <Icon as={GiClout} position="absolute" top={-8} left={-8} boxSize="180px" color="whiteAlpha.100" />
              <Box position="relative">
                <Box
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  h="72px"
                  w="72px"
                  borderRadius="2xl"
                  bg="whiteAlpha.200"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  mb={6}
                >
                  <Icon as={FaMapMarkerAlt} boxSize={8} color="gold.300" />
                </Box>
                <Heading size="lg" mb={2}>
                  Province de l'Ituri
                </Heading>
                <Text opacity={0.9} fontSize="sm">
                  Territoire d'Irumu (Village Tsere)
                </Text>
              </Box>
            </Box>
          </Reveal>
        </Container>
      </Box>

      {/* ═══════════════════════ IMAGE ═══════════════════════ */}
      <Box pb={{ base: 16, md: 24 }} bg="white">
        <Container maxW="7xl">
          <Reveal>
            <Box
              h={{ base: '280px', md: '440px' }}
              bgImage={`url(${industrieImage})`}
              bgSize="cover"
              bgPosition="center"
              borderRadius="2xl"
              position="relative"
              overflow="hidden"
              boxShadow="lift"
              transition="all 0.4s"
              _hover={{ transform: 'translateY(-4px)' }}
            >
              <Box position="absolute" inset={0} bgGradient="linear(to-t, rgba(8,28,52,0.92), rgba(8,28,52,0.25))" />
              <VStack position="absolute" bottom={6} left={6} right={6} align="start" spacing={1}>
                <Text fontWeight="800" fontSize="lg" color="white">
                  Clouterie TRIMER SARL
                </Text>
                <Text fontSize="sm" color="whiteAlpha.800">
                  Production industrielle de qualité
                </Text>
              </VStack>
            </Box>
          </Reveal>
        </Container>
      </Box>

      {/* ═══════════════════════ CTA ═══════════════════════ */}
      <Box py={{ base: 16, md: 20 }} bg="white">
        <Container maxW="4xl">
          <Reveal>
            <Box
              borderRadius="3xl"
              bgGradient="linear(120deg, navy.700, navy.900 60%, brand.800)"
              color="white"
              px={{ base: 8, md: 14 }}
              py={{ base: 12, md: 16 }}
              textAlign="center"
              position="relative"
              overflow="hidden"
              boxShadow="lift"
            >
              <Box position="absolute" inset={0} className="bg-grid" opacity={0.3} />
              <Box
                position="absolute"
                bottom="-90px"
                right="-60px"
                w="260px"
                h="260px"
                borderRadius="full"
                bgGradient="radial(circle at center, rgba(127,163,204,0.35), transparent 70%)"
              />

              <VStack spacing={6} position="relative">
                <Badge bg="whiteAlpha.200" color="white" px={4} py={1.5} fontSize="xs" letterSpacing="0.12em">
                  Clouterie
                </Badge>
                <Heading size="xl" color="white" fontWeight="800">
                  Besoin de clous de qualité ?
                </Heading>
                <Text opacity={0.9} maxW="xl">
                  Faites confiance à notre clouterie pour approvisionner votre quincaillerie,
                  chantier ou atelier de menuiserie.
                </Text>
                <Button
                  as={RouterLink}
                  to="/contact"
                  size="lg"
                  bg="white"
                  color="navy.800"
                  rightIcon={<FaArrowRight />}
                  _hover={{ bg: 'gray.100', transform: 'translateY(-2px)' }}
                >
                  Commander maintenant
                </Button>
              </VStack>
            </Box>
          </Reveal>
        </Container>
      </Box>
    </Box>
  );
}

export default Industries;
